import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ArticleGrid from '../components/home/ArticleGrid';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useSearch } from '../hooks/useSearch';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('query') || '');
  const [tempSearchTerm, setTempSearchTerm] = useState(searchTerm);
  
  const currentPage = parseInt(searchParams.get('page') || '1');
  const { 
    results, 
    loading, 
    error, 
    totalPages, 
    totalResults,
    searchArticles 
  } = useSearch();

  useEffect(() => {
    const query = searchParams.get('query');
    if (query && query.trim()) {
      const trimmedQuery = query.trim();
      setSearchTerm(trimmedQuery);
      setTempSearchTerm(trimmedQuery);
      searchArticles(trimmedQuery, currentPage);
    } else {
      // Reset si pas de query valide
      setSearchTerm('');
      setTempSearchTerm('');
    }
  }, [searchParams, currentPage, searchArticles]);

  // Mise à jour du titre de la page
  useEffect(() => {
    if (searchTerm && searchTerm.trim()) {
      document.title = `Recherche: ${searchTerm} - Xibaar Bambouck`;
    } else {
      document.title = 'Recherche - Xibaar Bambouck';
    }
    
    return () => {
      document.title = 'Xibaar Bambouck';
    };
  }, [searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (tempSearchTerm && tempSearchTerm.trim()) {
      setSearchParams({ query: tempSearchTerm.trim(), page: '1' });
    }
  };

  const handlePageChange = (page) => {
    if (searchTerm && searchTerm.trim()) {
      setSearchParams({ query: searchTerm.trim(), page: page.toString() });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clearSearch = () => {
    setTempSearchTerm('');
    setSearchTerm('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Rechercher des articles
            </h1>
            
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  value={tempSearchTerm || ''}
                  onChange={(e) => setTempSearchTerm(e.target.value || '')}
                  placeholder="Tapez votre recherche..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute right-3 top-3 flex items-center space-x-2">
                  {tempSearchTerm && tempSearchTerm.trim() && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  <button
                    type="submit"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>

            {searchTerm && searchTerm.trim() && (
              <div className="text-center mb-6">
                <p className="text-gray-600">
                  Résultats pour <span className="font-semibold">"{searchTerm}"</span>
                </p>
                {totalResults > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {totalResults} résultat{totalResults > 1 ? 's' : ''} trouvé{totalResults > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {loading && <LoadingSpinner />}
        
        {error && (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Erreur de recherche</h3>
            <p className="text-gray-600">{error}</p>
          </div>
        )}

        {!loading && !error && searchTerm && searchTerm.trim() && (
          <>
            {results && results.length > 0 ? (
              <>
                <ArticleGrid articles={results} />
                
                {totalPages > 1 && (
                  <div className="mt-12 flex justify-center">
                    <nav className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded ${
                          currentPage === 1
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border'
                        }`}
                      >
                        Précédent
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-4 py-2 rounded ${
                            page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded ${
                          currentPage === totalPages
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border'
                        }`}
                      >
                        Suivant
                      </button>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Aucun résultat trouvé
                </h3>
                <p className="text-gray-600 mb-4">
                  Essayez avec des mots-clés différents ou vérifiez l'orthographe.
                </p>
                <button
                  onClick={clearSearch}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                >
                  Nouvelle recherche
                </button>
              </div>
            )}
          </>
        )}

        {(!searchTerm || !searchTerm.trim()) && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Rechercher des articles
            </h3>
            <p className="text-gray-600">
              Utilisez le champ de recherche ci-dessus pour trouver des articles.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
